import React, { useEffect, useState } from "react";
import { Table, Input, Button, Space, Tag, message, Popover } from "antd";

import startCase from "lodash/startCase";
import { Typography } from "antd";
import { Helmet } from "react-helmet";
import UserForm from "../components/Users/UserForm";
import axios from "axios";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import RequirePermission from "../components/Common/RequirePermission";
import { useLocation, useNavigate } from "react-router-dom";
import LocationFilter from "../components/Common/LocationFilter";
import CityFilter from "../components/Common/CityFilter";
import { setLocalStorageJSONData } from "../utils/Misc";
const { Title } = Typography;

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [editPermission, setEditPermission] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");
  const [locations, setLocations] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);

  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    const newQueryParams = {
      city: selectedCity,
      location: selectedLocation,
      filter: value,
      limit: pageSize,
      page: 1,
    }

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleLocationSearch = (value) => {
    setSelectedLocation(value);
    const newQueryParams = {
      city: selectedCity,
      location: value,
      filter: filterText,
      limit: pageSize,
      page: 1,
    }

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleCitySearch = (value) => {
    setSelectedCity(value);
    const filteredLocations = locations
      .filter((location) => location.city_id === value)
      .map((location) => ({
        id: location.id,
        name: location.name,
      }));
    if (filteredLocations.length === 0) {
      setLocationOptions(locations);
    }
    else {
      setLocationOptions(filteredLocations);
    }
    const newQueryParams = {
      city: value,
      location: selectedLocation,
      filter: filterText,
      limit: pageSize,
      page: 1,
    }

    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // if (typeof sorter.order === "undefined") {
    //   setSortingOrder("desc");
    //   setSortingColumn("id");
    // } else {
    //   setSortingColumn(sorter.field);
    //   setSortingOrder(sorter.order === "ascend" ? "asc" : "desc");
    // }
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record) => {
    setIsModalOpen(true);
    setEditingUser(record);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/users`,
        {
          params: {
            filter: filterText, // add filter if needed
            limit: pageSize,
            page: currentPage,
            location: selectedLocation,
            city: selectedCity,
          },
        }
      );
      let userData = response.data.data.users;
      setTotalRecords(response.data.data.total);
      const masterLocations = JSON.parse(localStorage.getItem("locationMaster")) || [];

      userData = userData.map(user => {
        if (user.location_count === 1 && typeof user.locations === "string") {
          const loc = masterLocations.find(l => l.name === user.locations);
          console.log(loc);
          return {
            ...user,
            locationDetails: loc ? [loc] : [{name: user.locations, code: ""}],
          };
        } else if (user.location_count > 1 && Array.isArray(user.locations)) {
          const locs = masterLocations.filter(l =>
            user.locations.includes(l.id)
          );
          return {
            ...user,
            locationDetails: locs,
          };
        } else {
          return {
            ...user,
            locationDetails: [],
          };
        }
      });
      setUsers(userData);
      const location = JSON.parse(localStorage.getItem('locations'));
      let city = [];
      for (let i = 0; i < location.length; i++) {
        city.push({
          id: location[i].city_id,
          name: location[i].city_name,
        });
      };
      const uniqueCities = Array.from(
        new Map(city.map((item) => [item.id, item])).values()
      );
      setLocations(location);
      setCities(uniqueCities);
      if (!selectedCity) {
        setLocationOptions(location);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching users data", error);
    }
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/users/masters`
      );
      const masterLocations = response.data.data.locations;
      setLocalStorageJSONData("locationMaster", masterLocations);
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  }

  useEffect(() => {
    const newQueryParams = {
      city: selectedCity,
      location: selectedLocation,
      filter: filterText,
      limit: queryParams.get("limit") ?? 10,
      page: queryParams.get("page") ?? 1,
    }

    if (JSON.stringify(newQueryParams) !== JSON.stringify(Object.fromEntries(new URLSearchParams(location.search)))) {
      setQueryParams(newQueryParams);
    }

    setQueryParams(newQueryParams);
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedLocation, selectedCity]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    setUser(storedUser);
    const permissions = storedUser.permissions;
    setEditPermission(parseInt(permissions["users.edit"]) === 1);
  }, []);

  useEffect(() => {
    fetchMasterData();
  }, []);

  const columns = [
    {
      title: "Name",
      dataIndex: "first_name",
      render: (text, record) => (
        <span className="text-small">{`${startCase(
          record.first_name
        )} ${startCase(record.last_name)}`}</span>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      render: (text) => (
        <span className="text-small">{text && text.toLowerCase()}</span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role_name",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    },
    {
      title: "Locations",
      dataIndex: "location_count",
      render: (text, record) => {
        console.log(record);
        if (text === 0) {
          return <Tag color="red">All Locations</Tag>;
        }

        if (text === 1) {
          return <Tag color="green">{record.locationDetails[0].name}</Tag>;
        }
        const sortedLocations = [...record.locationDetails].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        return (
          <Popover
            placement="right"
            content={
              <Table
                loading={loading}
                columns={popOverColumns}
                dataSource={sortedLocations}
                size="small"
                pagination={false}
                scroll={{ y: 400 }}
                style={{ width: "300px" }}
                rowKey="id"
              />
            }
            title="Assigned Locations"
            trigger="hover"
          >
            <Tag style={{ cursor: "pointer" }} color="blue">
              {`${text} Locations`}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: "Last Logged In At",
      dataIndex: "last_login",
      render: (text) => {
        if (text) {
          return (
            <span className="text-small">
              {dayjs(text).format("DD-MM-YYYY HH:mm")}
            </span>
          );
        } else {
          return <span className="text-small">Never</span>;
        }
      },
    },
    {
      title: "Status",
      dataIndex: "activated",
      render: (status) => (
        <Tag color={parseInt(status) === 1 ? "green" : "red"}>
          {parseInt(status) === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
  ];
  if (editPermission) {
    columns.push({
      title: "Actions",
      render: (_, record) => (
        <Space>
          {user.id !== record.id && (
            <Button
              size="small"
              title="Edit User"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          )}
        </Space>
      ),
    });
  }

  const popOverColumns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <span className="text-small">{startCase(text)}</span>,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (text) => <span className="text-small">{text?.toUpperCase()}</span>,
    },
  ];

  return (
    <RequirePermission permission="users.view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Users`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Users
        </Title>
        <div className="d-flex justify-content gap-2 mb-3">
          <Input.Search
            placeholder="Search user"
            value={filterText}
            onChange={handleSearch}
            style={{ width: 200 }}
          />
          <CityFilter cities={cities} citySearch={selectedCity} handleCityChange={handleCitySearch} />
          <LocationFilter locationOptions={locationOptions} locationSearch={selectedLocation} handleLocationChange={handleLocationSearch} />
        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={users}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
            showSizeChanger: true, // Enables the page size selection dropdown
            pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
          }}
        />
        {isModalOpen && (
          <UserForm
            isModalOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            editingUser={editingUser}
            handleUpdateList={() => fetchData()}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Users;
